import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { Printer, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function RestockList() {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [items, setItems] = useState(null);
  const [qtyBeli, setQtyBeli] = useState({});
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/restock-list", { params: { branch_id: branchId } })
      .then((r) => {
        setItems(r.data.items);
        const q = {};
        r.data.items.forEach((i) => { q[i.id] = i.saran_qty; });
        setQtyBeli(q);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat daftar restock"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  useEffect(load, [branchId]);

  const branchLabel = branchId ? (branches.find((b) => b.id === branchId)?.name || "") : "Semua Cabang";
  const totalEst = useMemo(() => (items || []).reduce((s, i) => s + (Number(qtyBeli[i.id]) || 0) * i.harga_modal, 0), [items, qtyBeli]);

  const printList = () => {
    document.body.classList.add("printing-restock");
    window.print();
    setTimeout(() => document.body.classList.remove("printing-restock"), 500);
  };

  const downloadPdf = () => {
    if (!items) return;
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("FASMART — Daftar Belanja Restock", 14, 15);
    doc.setFontSize(9);
    doc.text(`Cabang: ${branchLabel} · Dibuat: ${new Date().toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })} WIB`, 14, 21);
    autoTable(doc, {
      startY: 26,
      head: [["Produk", "Stok", "Terjual 30hr", "Qty Beli", "Modal", "Estimasi"]],
      body: items.map((i) => [i.name.slice(0, 45), String(i.stock), String(i.sold30), String(qtyBeli[i.id] ?? i.saran_qty), rupiah(i.harga_modal), rupiah((Number(qtyBeli[i.id]) || 0) * i.harga_modal)]),
      foot: [["TOTAL ESTIMASI BELANJA", "", "", "", "", rupiah(totalEst)]],
      styles: { fontSize: 7 },
      headStyles: { fillColor: [5, 122, 85] },
      footStyles: { fillColor: [240, 240, 235], textColor: [20, 20, 20], fontStyle: "bold" },
    });
    doc.save(`daftar-belanja_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("PDF daftar belanja diunduh");
  };

  return (
    <div className="space-y-4" data-testid="restock-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h4 className="font-display font-bold text-lg">🛒 Daftar Belanja Restock</h4>
          <p className="text-xs text-stone-500">Otomatis berisi produk stok &lt;5 atau yang terjual cepat (stok &lt; ¼ penjualan 30 hari). Atur Qty Beli, lalu cetak/PDF untuk dibawa ke grosir.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select data-testid="restock-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} className="text-sm border border-[#E5DECF] rounded-full px-3 py-2 bg-white">
            <option value="">Semua Cabang</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <button data-testid="restock-refresh" onClick={load} className="px-3 py-2 rounded-full bg-white border border-[#E5DECF] text-xs font-bold flex items-center gap-1 hover:bg-stone-50"><RefreshCw className="w-3.5 h-3.5" /> Muat Ulang</button>
          {items && items.length > 0 && (
            <>
              <button data-testid="restock-print-btn" onClick={printList} className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm font-bold flex items-center gap-1.5 hover:bg-emerald-700 transition"><Printer className="w-4 h-4" /> Cetak</button>
              <button data-testid="restock-pdf-btn" onClick={downloadPdf} className="px-4 py-2 rounded-full bg-emerald-700 text-white text-sm font-bold flex items-center gap-1.5 hover:bg-emerald-800 transition"><Download className="w-4 h-4" /> PDF</button>
            </>
          )}
        </div>
      </div>

      {loading && <p className="text-sm text-stone-400">Memuat…</p>}
      {items && (
        <div id="restock-print" data-testid="restock-print">
          <div className="print-only" style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 900, fontSize: 18 }}>FASMART — Daftar Belanja Restock</div>
            <div style={{ fontSize: 12 }}>Cabang: {branchLabel} · Dibuat: {new Date().toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })} WIB</div>
          </div>
          <div className="bg-white rounded-2xl border border-[#E5DECF] p-4 flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm font-bold text-stone-600">{items.length} produk perlu direstock</span>
            <span className="font-display font-black text-lg text-emerald-700" data-testid="restock-total">Estimasi: {rupiah(totalEst)}</span>
          </div>
          <div className="bg-white rounded-2xl border border-[#E5DECF] overflow-x-auto mt-3">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-stone-500 border-b border-[#E5DECF]"><th className="p-3">Produk</th><th className="p-3 text-right">Stok</th><th className="p-3 text-right">Terjual 30hr</th><th className="p-3 text-right w-24">Qty Beli</th><th className="p-3 text-right">Modal</th><th className="p-3 text-right">Estimasi</th></tr></thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} className="border-b border-stone-100 last:border-0" data-testid={`restock-row-${i.id}`}>
                    <td className="p-3 text-xs font-semibold">{i.name}<span className="block text-[10px] font-normal text-stone-400">{i.category} · {i.unit}</span></td>
                    <td className={`p-3 text-right text-xs font-bold ${i.stock <= 0 ? "text-rose-600" : "text-amber-600"}`}>{i.stock}</td>
                    <td className="p-3 text-right text-xs">{i.sold30}</td>
                    <td className="p-3 text-right"><input data-testid={`restock-qty-${i.id}`} type="number" min="0" value={qtyBeli[i.id] ?? i.saran_qty} onChange={(e) => setQtyBeli({ ...qtyBeli, [i.id]: e.target.value })} className="w-16 text-right text-xs font-bold border border-[#E5DECF] rounded-lg px-1.5 py-1 bg-white" /></td>
                    <td className="p-3 text-right text-xs text-stone-500">{rupiah(i.harga_modal)}</td>
                    <td className="p-3 text-right text-xs font-bold">{rupiah((Number(qtyBeli[i.id]) || 0) * i.harga_modal)}</td>
                  </tr>
                ))}
                {!items.length && <tr><td colSpan={6} className="p-4 text-stone-400 text-xs">✓ Semua stok aman — tidak ada yang perlu direstock.</td></tr>}
              </tbody>
              {items.length > 0 && (
                <tfoot><tr className="border-t-2 border-stone-900 font-black text-xs"><td className="p-3" colSpan={5}>TOTAL ESTIMASI BELANJA</td><td className="p-3 text-right text-emerald-700">{rupiah(totalEst)}</td></tr></tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
